import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import './PatientDetail.css';  // We'll create this file next
import HumanBody from '../../components/HumanBody/HumanBody';
import { fetchPatientOverview, fetchPatientTimeline, fetchMedicationGroupList } from '../../api/reportService';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Date range filter state for Historical Data tab
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // API data state for Overview tab
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Timeline data state for Historical Data tab
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);

  // Medication list state
  const [medicationList, setMedicationList] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(false);

  // Fetch patient overview data from API
  const loadPatientOverview = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      console.log('[PatientDetail] Fetching overview for patient:', id);

      const response = await fetchPatientOverview(id);

      if (response.success) {
        setOverviewData(response.data);
        console.log('[PatientDetail] Overview data loaded successfully');
        console.log('[PatientDetail] Demographics data:', JSON.stringify(response.data?.demographics, null, 2));
      } else {
        throw new Error('Failed to load patient overview');
      }
    } catch (err) {
      console.error('[PatientDetail] Error loading overview:', err);
      setError(err.message || 'Failed to load patient data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load data on component mount and when patient ID changes
  useEffect(() => {
    loadPatientOverview();
  }, [loadPatientOverview]);

  // Fetch patient timeline data from API
  const loadPatientTimeline = useCallback(async () => {
    if (!id) return;

    try {
      setTimelineLoading(true);
      setTimelineError(null);

      // Build date range options
      const options = {};
      if (startDate) options.startDate = startDate;
      if (endDate) options.endDate = endDate;

      console.log('[PatientDetail] Loading timeline with date range:', options);

      const data = await fetchPatientTimeline(id, options);

      setTimelineData(data);
    } catch (err) {
      console.error('Error loading timeline:', err);
      setTimelineError(err.message);
    } finally {
      setTimelineLoading(false);
    }
  }, [id, startDate, endDate]);

  // Fetch medication group list from API
  const loadMedicationList = useCallback(async () => {
    try {
      setMedicationsLoading(true);
      console.log('[PatientDetail] Fetching medication list');

      const data = await fetchMedicationGroupList();

      // Extract medication names and normalize them
      const medications = data.map(med => {
        // Extract base medication name (remove dosage suffix)
        const baseName = med._id.split('-')[0];
        // Capitalize first letter
        return baseName.charAt(0).toUpperCase() + baseName.slice(1);
      });

      // Remove duplicates (e.g., haegarda-2000 and haegarda-3000 both become "Haegarda")
      const uniqueMedications = [...new Set(medications)];

      setMedicationList(uniqueMedications);
      console.log('[PatientDetail] Medication list loaded:', uniqueMedications);
    } catch (err) {
      console.error('Error loading medication list:', err);
      // Fallback to default medications if API fails
      setMedicationList(['Takhzyro', 'Haegarda', 'Icatibant', 'Berinert']);
    } finally {
      setMedicationsLoading(false);
    }
  }, []);

  // Load medication list on component mount
  useEffect(() => {
    loadMedicationList();
  }, [loadMedicationList]);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'overview') {
      // Reload overview data when switching to overview tab
      loadPatientOverview();
    } else if (activeTab === 'history') {
      // Load timeline data when switching to historical tab
      // Only reload if both dates are selected or both are empty
      const bothDatesSelected = startDate && endDate;
      const bothDatesEmpty = !startDate && !endDate;

      if (bothDatesSelected || bothDatesEmpty) {
        loadPatientTimeline();
      }
    }
  }, [activeTab, id, startDate, endDate, loadPatientOverview, loadPatientTimeline]);

  // Placeholder patient data - for basic info display
  // Get patient name and number from overviewData
  const patientName = overviewData?.demographics?.name || 'Loading...';
  const patientNumber = overviewData?.demographics?.patientNumber || id;

  // Helper function to generate colors dynamically
  const generateColors = (count) => {
    const baseColors = [
      '#938AF5', '#75C7B8', '#86E26D', '#F4B266', '#FFE066',
      '#90E0EF', '#B5E48C', '#FFB4A2', '#CDB4DB', '#98D8AA',
      '#748DA6', '#89CFF0', '#FF9F40', '#FFE135', '#4BC0C0'
    ];
    return Array.from({ length: count }, (_, i) => baseColors[i % baseColors.length]);
  };

  // Helper function to convert to title case
  const toTitleCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Signs and Symptoms Chart Data (from API)
  const symptomTotal = overviewData?.symptomAnalysis?.reduce((sum, s) => sum + (s.count || 0), 0) || 0;
  const barChartData = overviewData?.symptomAnalysis && overviewData.symptomAnalysis.length > 0 ? {
    labels: overviewData.symptomAnalysis.map(s => toTitleCase(s.symptom)),
    datasets: [{
      label: 'Percentage of Cases',
      data: overviewData.symptomAnalysis.map(s => symptomTotal > 0 ? Math.round((s.count / symptomTotal) * 10000) / 100 : Math.round(parseFloat(s.percentage) * 100) / 100),
      backgroundColor: generateColors(overviewData.symptomAnalysis.length).map(color =>
        color.replace(')', ', 0.8)').replace('rgb', 'rgba')
      ),
      borderColor: generateColors(overviewData.symptomAnalysis.length),
      borderWidth: 1,
      borderRadius: 4,
      barThickness: 20,
    }]
  } : {
    labels: [],
    datasets: [{
      label: 'Percentage of Cases',
      data: [],
      backgroundColor: [],
      borderColor: [],
      borderWidth: 1,
      borderRadius: 4,
      barThickness: 20,
    }]
  };

  const barChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#000',
        bodyColor: '#666',
        bodyFont: {
          size: 12,
          family: "'Mulish', sans-serif"
        },
        padding: 12,
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        displayColors: true,
        callbacks: {
          label: (context) => {
            return `${parseFloat(context.parsed.x).toFixed(2)}% of cases`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            size: 12
          },
          color: '#666'
        },
        max: 100
      },
      y: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            size: 12
          },
          color: '#666'
        }
      }
    }
  };

  // Treatment Location Chart Data (from API)
  const locationTotal = overviewData?.treatmentLocations?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
  const locationChartData = overviewData?.treatmentLocations ? {
    labels: overviewData.treatmentLocations.map(item => item.location),
    datasets: [
      {
        data: overviewData.treatmentLocations.map(item => locationTotal > 0 ? Math.round((item.count / locationTotal) * 10000) / 100 : Math.round(parseFloat(item.percentage) * 100) / 100),
        backgroundColor: generateColors(overviewData.treatmentLocations.length),
        borderWidth: 0,
      },
    ],
  } : {
    labels: [],
    datasets: [{ data: [] }]
  };

  const locationChartOptions = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      }
    },
    cutout: '70%',
    maintainAspectRatio: false
  };



  // Time period options
  const timePeriods = [
    { value: '2w', label: '2 WEEKS' },
    { value: '1m', label: '1 MONTH' },
    { value: '3m', label: '3 MONTHS' },
    { value: '6m', label: '6 MONTHS' },
    { value: '12m', label: '12 MONTHS' },
    { value: '18m', label: '18 MONTHS' },
  ];

  // Add this after the patient state data
  const medicationData = {
    labels: ['Berinert 1500', 'Berinert 500', 'Cinryze', 'Firazyr', 'Haegarda-2000', 'Haegarda-3000', 'Orladeyo', 'Takhzyro'],
    datasets: [{
      data: [1.6, 6.6, 14.8, 19.7, 11.5, 3.3, 23, 19.7],
      backgroundColor: [
        '#F4B266', // Berinert 1500
        '#F9E076', // Berinert 500
        '#86E26D', // Cinryze
        '#96D67E', // Firazyr
        '#75C7B8', // Haegarda-2000
        '#5A6B6A', // Haegarda-3000
        '#7CC5E9', // Orladeyo
        '#4355DB'  // Takhzyro
      ],
      borderWidth: 0,
      cutout: '75%'
    }]
  };

  const doughnutOptions = {
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        bodyFont: {
          size: 12,
          family: "'Mulish', sans-serif"
        },
        padding: 12,
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        displayColors: true,
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.raw}%`;
          }
        }
      }
    },
    cutout: '75%',
    radius: '90%',
    maintainAspectRatio: false
  };

  // Update the shared options for all donut charts
  const sharedDonutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      intersect: true
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 10
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: {
          usePointStyle: true,
          padding: 12,
          boxWidth: 10,
          boxHeight: 10,
          font: {
            size: 11,
            family: "'Mulish', sans-serif",
            weight: '500'
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            if (!datasets || !datasets[0] || !datasets[0].data) return [];

            // Helper function to convert to title case
            const toTitleCase = (str) => {
              if (!str) return '';
              return str.toLowerCase().split(' ').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
            };

            return datasets[0].data.map((value, i) => ({
              text: `${toTitleCase(chart.data.labels[i])} (${parseFloat(value).toFixed(2)}%)`,
              fillStyle: datasets[0].backgroundColor[i],
              index: i
            }));
          }
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        bodyFont: {
          size: 12,
          family: "'Mulish', sans-serif"
        },
        padding: 12,
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        displayColors: true,
        callbacks: {
          label: (context) => {
            return `${context.label}: ${parseFloat(context.raw).toFixed(2)}%`;
          }
        }
      }
    },
    cutout: '70%',
    radius: '85%',
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: '#fff',
        hoverBorderWidth: 3,
        hoverBorderColor: '#fff',
        hoverOffset: 8
      }
    }
  };

  // Update individual chart options to use shared options
  const triggersOptions = {
    ...sharedDonutOptions,
    plugins: {
      ...sharedDonutOptions.plugins
    }
  };

  const medicationHAEOptions = {
    ...sharedDonutOptions,
    plugins: {
      ...sharedDonutOptions.plugins
    }
  };

  const attacksOptions = {
    ...sharedDonutOptions,
    plugins: {
      ...sharedDonutOptions.plugins
    }
  };

  const missedDaysOptions = {
    ...sharedDonutOptions,
    plugins: {
      ...sharedDonutOptions.plugins
    }
  };

  // Categorize areas affected into three main groups
  const categorizeAreasAffected = (attackLocations) => {
    if (!attackLocations || attackLocations.length === 0) {
      return { laryngealTotal: 0, abdominalTotal: 0, peripheralTotal: 0 };
    }

    let laryngealCount = 0;
    let abdominalCount = 0;
    let peripheralCount = 0;
    let grandTotal = 0;

    attackLocations.forEach(item => {
      const area = item.location.toLowerCase();
      const count = item.count;
      grandTotal += count;

      // Categorize based on area name (matching mimonitor logic)
      if (['face', 'tongue', 'mouth', 'larynx'].includes(area)) {
        laryngealCount += count;
      } else if (['abdomen', 'chest', 'genitals'].includes(area)) {
        abdominalCount += count;
      } else {
        // All other areas go to peripheral
        peripheralCount += count;
      }
    });

    // Calculate percentages
    const laryngealPercentage = grandTotal > 0 ? Math.round((laryngealCount / grandTotal) * 10000) / 100 : 0;
    const abdominalPercentage = grandTotal > 0 ? Math.round((abdominalCount / grandTotal) * 10000) / 100 : 0;
    const peripheralPercentage = grandTotal > 0 ? Math.round((peripheralCount / grandTotal) * 10000) / 100 : 0;

    return {
      laryngealTotal: laryngealPercentage,
      abdominalTotal: abdominalPercentage,
      peripheralTotal: peripheralPercentage
    };
  };

  const categorizedAreas = categorizeAreasAffected(overviewData?.attackLocations);

  // Areas Affected Chart Data - showing 3 categories for Overview tab
  const areasAffectedCategorizedData = categorizedAreas.laryngealTotal > 0 || categorizedAreas.abdominalTotal > 0 || categorizedAreas.peripheralTotal > 0 ? {
    labels: ['Laryngeal/Head', 'Abdominal', 'Peripheral'],
    datasets: [{
      data: [categorizedAreas.laryngealTotal, categorizedAreas.abdominalTotal, categorizedAreas.peripheralTotal],
      backgroundColor: ['#8B9DC8', '#95C0BE', '#A8D5BA'],
      borderWidth: 0,
      cutout: '75%'
    }]
  } : {
    labels: [],
    datasets: [{ data: [] }]
  };

  // Areas Affected Chart Data (from API) - for Historical tab (all individual areas)
  const attackLocationTotal = overviewData?.attackLocations?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
  const areasAffectedData = overviewData?.attackLocations ? {
    labels: overviewData.attackLocations.map(item => item.location),
    datasets: [{
      data: overviewData.attackLocations.map(item => attackLocationTotal > 0 ? Math.round((item.count / attackLocationTotal) * 10000) / 100 : Math.round(parseFloat(item.percentage) * 100) / 100),
      backgroundColor: generateColors(overviewData.attackLocations.length),
      borderWidth: 0,
      cutout: '75%'
    }]
  } : {
    labels: [],
    datasets: [{ data: [] }]
  };

  // Medication HAE Chart Data (from API) - Calculate percentages
  // Medications to exclude from display
  const excludedMedications = ['cinryze'];
  const medicationHAEData = overviewData?.medicationStats ? (() => {
    // Filter out excluded medications
    const filteredMedStats = overviewData.medicationStats.filter(
      item => !excludedMedications.includes(item.medicationName.toLowerCase())
    );
    const totalUsage = filteredMedStats.reduce((sum, item) => sum + item.usageCount, 0);
    return {
      labels: filteredMedStats.map(item => item.medicationName),
      datasets: [{
        data: filteredMedStats.map(item => {
          const percentage = totalUsage > 0 ? ((item.usageCount / totalUsage) * 100) : 0;
          return Math.round(percentage * 100) / 100;
        }),
        backgroundColor: generateColors(filteredMedStats.length),
        borderWidth: 0,
        spacing: 2,
        hoverOffset: 15
      }]
    };
  })() : {
    labels: [],
    datasets: [{ data: [] }]
  };

  // Triggers Chart Data (from API)
  const triggerTotal = overviewData?.triggerAnalysis?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
  const triggersData = overviewData?.triggerAnalysis ? {
    labels: overviewData.triggerAnalysis.map(item => item.trigger),
    datasets: [{
      data: overviewData.triggerAnalysis.map(item => triggerTotal > 0 ? Math.round((item.count / triggerTotal) * 10000) / 100 : Math.round(parseFloat(item.percentage) * 100) / 100),
      backgroundColor: generateColors(overviewData.triggerAnalysis.length),
      borderWidth: 0,
      spacing: 2,
      hoverOffset: 15
    }]
  } : {
    labels: [],
    datasets: [{ data: [] }]
  };

  // Days Missed Data (from API)
  const missedDaysTotal = overviewData?.daysMissedDistribution?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
  const missedDaysData = overviewData?.daysMissedDistribution ? {
    labels: overviewData.daysMissedDistribution.map(item => item.range),
    datasets: [{
      data: overviewData.daysMissedDistribution.map(item => missedDaysTotal > 0 ? Math.round((item.count / missedDaysTotal) * 10000) / 100 : Math.round(parseFloat(item.percentage) * 100) / 100),
      backgroundColor: generateColors(overviewData.daysMissedDistribution.length),
      borderWidth: 0,
      spacing: 2,
      hoverOffset: 15
    }]
  } : {
    labels: [],
    datasets: [{ data: [] }]
  };

  // Treatment Classification Data (from API)
  const classificationTotal = overviewData?.treatmentClassification?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
  const attacksData = overviewData?.treatmentClassification ? {
    labels: overviewData.treatmentClassification.map(item => toTitleCase(item.type)),
    datasets: [{
      data: overviewData.treatmentClassification.map(item => classificationTotal > 0 ? Math.round((item.count / classificationTotal) * 10000) / 100 : Math.round(parseFloat(item.percentage) * 100) / 100),
      backgroundColor: generateColors(overviewData.treatmentClassification.length),
      borderWidth: 0,
      spacing: 2,
      hoverOffset: 15
    }]
  } : {
    labels: [],
    datasets: [{ data: [] }]
  };

  // Timeline filtering function
  const getFilteredTimelineData = () => {
    // Return empty data if timeline hasn't loaded yet
    if (!timelineData) {
      return {
        prophylaxis: [],
        acute: [],
        attacks: []
      };
    }

    // If no date range selected OR only one date selected, show all data
    // We only filter when BOTH dates are selected
    if (!startDate || !endDate) {
      return {
        prophylaxis: timelineData.prophylaxisTreatments || [],
        acute: timelineData.acuteTreatments || [],
        attacks: timelineData.attackIntensity || []
      };
    }

    const rawProphylaxis = timelineData.prophylaxisTreatments || [];
    const rawAcute = timelineData.acuteTreatments || [];
    const rawAttacks = timelineData.attackIntensity || [];

    // Parse date range
    const filterStartDate = startDate ? new Date(startDate) : null;
    const filterEndDate = endDate ? new Date(endDate) : null;

    // Set end date to end of day (23:59:59)
    if (filterEndDate) {
      filterEndDate.setHours(23, 59, 59, 999);
    }

    // Helper to get date from item (handles different date field names)
    const getItemDate = (item) => {
      const dateValue = item.date || item.eventDate || item.treatmentDate || item.timestamp;
      if (!dateValue) return null;
      return new Date(dateValue);
    };

    // Filter by date range
    const filtered = {
      prophylaxis: rawProphylaxis.filter(item => {
        const itemDate = getItemDate(item);
        if (!itemDate || isNaN(itemDate.getTime())) return false;

        if (filterStartDate && itemDate < filterStartDate) return false;
        if (filterEndDate && itemDate > filterEndDate) return false;
        return true;
      }),
      acute: rawAcute.filter(item => {
        const itemDate = getItemDate(item);
        if (!itemDate || isNaN(itemDate.getTime())) return false;

        if (filterStartDate && itemDate < filterStartDate) return false;
        if (filterEndDate && itemDate > filterEndDate) return false;
        return true;
      }),
      attacks: rawAttacks.filter(item => {
        const itemDate = getItemDate(item);
        if (!itemDate || isNaN(itemDate.getTime())) return false;

        if (filterStartDate && itemDate < filterStartDate) return false;
        if (filterEndDate && itemDate > filterEndDate) return false;
        return true;
      })
    };

    return filtered;
  };

  // Helper function to get date from item (handles different date field names)
  const getItemDateValue = (item) => {
    // Try different possible date field names
    return item.date || item.eventDate || item.treatmentDate || item.timestamp;
  };

  // Helper function to safely parse dates to timestamp (combines date + time if available)
  const parseDate = (dateString, item = null) => {
    if (!dateString) return null;

    // If we have both date and time, combine them for accurate positioning
    if (item && item.time) {
      const dateTimeString = `${dateString}T${item.time}`;
      const parsed = new Date(dateTimeString);
      if (!isNaN(parsed.getTime())) {
        return parsed.getTime();
      }
    }

    // Fallback: Use noon (12:00:00) for items without time (like attacks)
    // This centers them on the day and aligns better with treatments
    const dateTimeString = `${dateString}T12:00:00`;
    const parsed = new Date(dateTimeString);
    if (isNaN(parsed.getTime())) return null;
    return parsed.getTime();
  };

  // Create timeline chart data
  const createTimelineChartData = () => {
    const filteredData = getFilteredTimelineData();
    const datasets = [];

    // Attack intensity stepped line (red stepped line)
    if (filteredData.attacks.length > 0) {
      datasets.push({
        label: 'Attack Intensity',
        data: filteredData.attacks.map(item => {
          const dateValue = getItemDateValue(item);
          const timestamp = parseDate(dateValue, item); // Backend now includes time field
          return {
            x: timestamp || dateValue, // Use timestamp if available, fallback to date string
            y: item.intensity,
            duration: item.duration,
            triggers: item.triggers,
            location: item.location
          };
        }).filter(item => item.x !== null),
        borderColor: 'rgba(231, 76, 60, 1)',
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        fill: false,
        stepped: 'before',
        pointRadius: 6,
        pointHoverRadius: 8,
        borderWidth: 3,
        tension: 0,
        showLine: true
      });
    }

    // Process prophylaxis medications (Takhzyro, Haegarda, Berinert, Icatibant, Orladeyo, Cinryze, Firazyr)
    // Define Y-axis position for each medication type
    const prophylaxisMedicationYLevels = {
      'Haegarda': 10.5,    // Teal circles
      'Takhzyro': 11,      // Purple triangles
      'Berinert': 11.5,    // Green squares
      'Icatibant': 12,     // Orange diamonds
      'Firazyr': 12.5,     // Red diamonds
      'Cinryze': 13,       // Blue triangles
      'Orladeyo': 13.5     // Pink crosses
    };

    // Show medications (Cinryze excluded)
    const allowedProphylaxisMedications = ['Takhzyro', 'Haegarda', 'Berinert', 'Icatibant', 'Orladeyo', 'Firazyr'];

    // Group prophylaxis by medication from allMedications array
    const prophylaxisMedicationsMap = new Map();

    filteredData.prophylaxis.forEach(prophylaxisTx => {
      const dateValue = getItemDateValue(prophylaxisTx);
      const timestamp = parseDate(dateValue, prophylaxisTx); // Pass item to include time

      if (timestamp && prophylaxisTx.allMedications) {
        prophylaxisTx.allMedications.forEach(med => {
          const medName = med.name;

          // Skip if not in allowed medications list
          if (!allowedProphylaxisMedications.includes(medName)) {
            return;
          }

          if (!prophylaxisMedicationsMap.has(medName)) {
            prophylaxisMedicationsMap.set(medName, []);
          }

          // Get Y position based on medication type
          const yPosition = prophylaxisMedicationYLevels[medName] || 11;

          prophylaxisMedicationsMap.get(medName).push({
            x: timestamp,
            y: yPosition,
            medication: medName,
            dosage: `${med.dose} ${med.doseType}`,
            treatmentLocation: prophylaxisTx.treatmentLocation,
            injectionSite: prophylaxisTx.injectionSite,
            date: dateValue
          });
        });
      }
    });

    // Create datasets for each unique prophylaxis medication type
    const prophylaxisMedicationStyles = {
      'Takhzyro': { color: 'rgba(147, 51, 234, 1)', style: 'triangle', label: 'Takhzyro (Prophylaxis)' },
      'Haegarda': { color: 'rgba(20, 184, 166, 1)', style: 'circle', label: 'Haegarda (Prophylaxis)' },
      'Berinert': { color: 'rgba(34, 197, 94, 1)', style: 'rect', label: 'Berinert (Prophylaxis)' },
      'Icatibant': { color: 'rgba(251, 146, 60, 1)', style: 'rectRot', label: 'Icatibant (Prophylaxis)' },
      'Firazyr': { color: 'rgba(239, 68, 68, 1)', style: 'rectRot', label: 'Firazyr (Prophylaxis)' },
      'Cinryze': { color: 'rgba(59, 130, 246, 1)', style: 'triangle', label: 'Cinryze (Prophylaxis)' },
      'Orladeyo': { color: 'rgba(236, 72, 153, 1)', style: 'crossRot', label: 'Orladeyo (Prophylaxis)' }
    };

    prophylaxisMedicationsMap.forEach((dataPoints, medName) => {
      const style = prophylaxisMedicationStyles[medName] || {
        color: 'rgba(107, 114, 128, 1)',
        style: 'circle',
        label: `${medName} (Prophylaxis)`
      };

      datasets.push({
        label: style.label,
        data: dataPoints,
        borderColor: style.color,
        backgroundColor: style.color,
        pointStyle: style.style,
        pointRadius: 8,
        pointHoverRadius: 10,
        showLine: false,
        borderWidth: 1
      });
    });

    // NOTE: Acute treatment markers (Icatibant, Berinert, etc.) are handled
    // by the "allMedications" logic below to avoid duplicates
    // and to show ALL medications used during each acute treatment event

    // Add vials/medications for attacks (show all medications used during attacks)
    // Position each medication type at its corresponding Y-axis level
    if (filteredData.attacks.length > 0) {
      // Define Y-axis position for each medication type
      const medicationYLevels = {
        'Haegarda': 10.5,    // Same level as Haegarda prophylaxis
        'Takhzyro': 11,      // Same level as Takhzyro prophylaxis
        'Berinert': 11.5,    // Green squares
        'Icatibant': 12,     // Orange diamonds
        'Firazyr': 12.5,     // Red diamonds
        'Cinryze': 13,       // Blue triangles
        'Orladeyo': 13.5     // Pink crosses
      };

      // Group attacks by medication from allMedications in acuteTreatments
      const attackMedicationsMap = new Map();

      // Show all 7 medications for timeline display
      const allowedMedications = ['Takhzyro', 'Haegarda', 'Icatibant', 'Berinert', 'Orladeyo', 'Cinryze', 'Firazyr'];

      filteredData.acute.forEach(acuteTx => {
        const dateValue = getItemDateValue(acuteTx);
        const timestamp = parseDate(dateValue, acuteTx); // Pass item to include time

        if (timestamp && acuteTx.allMedications) {
          acuteTx.allMedications.forEach(med => {
            const medName = med.name;

            // Skip if not in allowed medications list
            if (!allowedMedications.includes(medName)) {
              return;
            }

            if (!attackMedicationsMap.has(medName)) {
              attackMedicationsMap.set(medName, []);
            }

            // Get Y position based on medication type
            const yPosition = medicationYLevels[medName] || 12.5;
console.log(timestamp,'timestamptimestamp')
            attackMedicationsMap.get(medName).push({
              x: timestamp,
              y: yPosition, // Position based on medication type
              medication: medName,
              dosage: `${med.dose} ${med.doseType}`,
              reason: acuteTx.reason,
              location: acuteTx.location,
              timeToTreatment: acuteTx.timeToTreatment,
              areasAffected: acuteTx.areasAffected,
              injectionSite: acuteTx.injectionSite,
              date: dateValue
            });
          });
        }
      });

      // Create datasets for each unique medication type (all 7 medications)
      const medicationStyles = {
        'Takhzyro': { color: 'rgba(147, 51, 234, 1)', style: 'triangle', label: 'Takhzyro (Attack)' },
        'Haegarda': { color: 'rgba(20, 184, 166, 1)', style: 'circle', label: 'Haegarda (Attack)' },
        'Icatibant': { color: 'rgba(251, 146, 60, 1)', style: 'rectRot', label: 'Icatibant (Attack)' },
        'Berinert': { color: 'rgba(34, 197, 94, 1)', style: 'rect', label: 'Berinert (Attack)' },
        'Firazyr': { color: 'rgba(239, 68, 68, 1)', style: 'rectRot', label: 'Firazyr (Attack)' },
        'Cinryze': { color: 'rgba(59, 130, 246, 1)', style: 'triangle', label: 'Cinryze (Attack)' },
        'Orladeyo': { color: 'rgba(236, 72, 153, 1)', style: 'crossRot', label: 'Orladeyo (Attack)' }
      };

      attackMedicationsMap.forEach((dataPoints, medName) => {
        const style = medicationStyles[medName] || {
          color: 'rgba(107, 114, 128, 1)',
          style: 'circle',
          label: `${medName} (Attack)`
        };

        datasets.push({
          label: style.label,
          data: dataPoints, // Don't apply jitter - keep on same vertical line
          borderColor: style.color,
          backgroundColor: style.color,
          pointStyle: style.style,
          pointRadius: 8,
          pointHoverRadius: 10,
          showLine: false,
          borderWidth: 1
        });
      });
    }
console.log(datasets,'datasetsdatasets')
    return { datasets };
  };

  // Get timeline chart options with dynamic bounds
  const getTimelineChartOptions = () => {
    const chartData = createTimelineChartData();
    const allDates = [];

    // Collect all unique dates from datasets
    chartData.datasets.forEach(dataset => {
      dataset.data.forEach(point => {
        if (point.x) {
          const date = new Date(point.x);
          const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          allDates.push(dateOnly.getTime());
        }
      });
    });

    // Get unique dates and sort
    const uniqueDates = [...new Set(allDates)].sort((a, b) => a - b);

    let min, max;
    if (uniqueDates.length > 0) {
      const firstDate = uniqueDates[0];
      const lastDate = uniqueDates[uniqueDates.length - 1];

      // Add 1 day padding
      const oneDayMs = 24 * 60 * 60 * 1000;
      min = firstDate - oneDayMs;
      max = lastDate + oneDayMs;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'point'
      },
      scales: {
        x: {
          type: 'time',
          min: min,
          max: max,
          time: {
            unit: 'day',
            displayFormats: {
              day: 'MMM d, yyyy'
            },
            tooltipFormat: 'MMM d, yyyy'
          },
          title: {
            display: true,
            text: 'Timeline',
            font: {
              size: 14,
              weight: 'bold',
              family: "'Mulish', sans-serif"
            }
          },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            font: {
              size: 12,
              family: "'Mulish', sans-serif"
            },
            autoSkip: false,
            maxTicksLimit: 10,
            maxRotation: 0,
            minRotation: 0
          }
        },
      y: {
        title: {
          display: true,
          text: 'Attack Intensity (0-10) / Treatment Types',
          font: {
            size: 13,
            weight: 'bold',
            family: "'Mulish', sans-serif"
          }
        },
        min: 0,
        max: 14,
        ticks: {
          font: {
            size: 11,
            family: "'Mulish', sans-serif",
            weight: '600'
          },
          // Include both whole numbers and medication-specific decimal values
          callback: function(value) {
            // Show intensity scale (0-10)
            if (Number.isInteger(value) && value >= 0 && value <= 10) {
              return value;
            }
            // Show medication levels with proper labels (Cinryze excluded)
            if (value === 10.5) return 'Haegarda';
            if (value === 11) return 'Takhzyro';
            if (value === 11.5) return 'Berinert';
            if (value === 12) return 'Icatibant';
            if (value === 12.5) return 'Firazyr';
            // if (value === 13) return 'Cinryze';  // Hidden
            if (value === 13.5) return 'Orladeyo';

            return '';
          },
          // Generate custom tick values to include medication levels
          autoSkip: false,
          includeBounds: true,
          color: '#374151',
          padding: 5
        },
        afterBuildTicks: function(axis) {
          // Add custom tick values for medications (Cinryze excluded)
          axis.ticks = [
            { value: 0 },
            { value: 1 },
            { value: 2 },
            { value: 3 },
            { value: 4 },
            { value: 5 },
            { value: 6 },
            { value: 7 },
            { value: 8 },
            { value: 9 },
            { value: 10 },
            { value: 10.5 }, // Haegarda
            { value: 11 },   // Takhzyro
            { value: 11.5 }, // Berinert
            { value: 12 },   // Icatibant
            { value: 12.5 }, // Firazyr
            // { value: 13 },   // Cinryze - Hidden
            { value: 13.5 }  // Orladeyo
          ];
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.08)',
          lineWidth: 1
        },
        position: 'left'
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          title: function(context) {
            const date = new Date(context[0].parsed.x);
            return date.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          },
          label: function(context) {
            const dataPoint = context.raw;
            const label = context.dataset.label;

            if (label === 'Attack Intensity') {
              return [
                `Intensity: ${dataPoint.y}/10`,
                `Duration: ${dataPoint.duration || 'N/A'}`,
                `Triggers: ${dataPoint.triggers || 'Unknown'}`,
                `Location: ${dataPoint.location || 'N/A'}`
              ];
            } else if (label.includes('(Attack)')) {
              // Attack medications (vials used during attack)
              const attackLines = [
                `Medication: ${dataPoint.medication || 'N/A'}`,
                `Dosage: ${dataPoint.dosage || 'N/A'}`,
                `Time to Treatment: ${dataPoint.timeToTreatment || 'N/A'}`,
                `Reason: ${dataPoint.reason || 'Not specified'}`,
                `Treatment Location: ${dataPoint.location || 'N/A'}`,
                `Areas Affected: ${dataPoint.areasAffected?.join(', ') || 'N/A'}`
              ];
              // Show injection site for injectable attack medications
              if (['Icatibant', 'Firazyr', 'Berinert', 'Haegarda', 'Takhzyro'].includes(dataPoint.medication)) {
                attackLines.push(`Injection Site: ${dataPoint.injectionSite || 'Not specified'}`);
              }
              return attackLines;
            } else if (label.includes('Prophylaxis')) {
              const tooltipLines = [
                `Medication: ${dataPoint.medication || 'N/A'}`,
                `Dosage: ${dataPoint.dosage || 'N/A'}`,
                `Treatment Location: ${dataPoint.treatmentLocation || 'Not specified'}`
              ];

              // Only show injection site for non-oral medications (exclude Orladeyo)
              if (dataPoint.medication && !dataPoint.medication.toLowerCase().includes('orladeyo')) {
                tooltipLines.push(`Injection Site: ${dataPoint.injectionSite || 'Not specified'}`);
              }

              return tooltipLines;
            } else if (label.includes('Acute')) {
              return [
                `Medication: ${dataPoint.medication || 'N/A'}`,
                `Dosage: ${dataPoint.dosage || 'N/A'}`,
                `Time to Treatment: ${dataPoint.timeToTreatment || 'N/A'}`,
                `Reason: ${dataPoint.reason || 'Not specified'}`,
                `Treatment Location: ${dataPoint.location || 'N/A'}`,
                `Areas Affected: ${dataPoint.areasAffected?.join(', ') || 'N/A'}`
              ];
            }
            return `${label}: ${dataPoint.y}`;
          }
        }
      }
    }
    };
  };

  // Helper function to generate legend configuration for medications
  const getMedicationLegendConfig = () => {
    const medicationConfig = {
      'Takhzyro': {
        color: 'rgba(147, 51, 234, 1)',
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-700',
        subTextColor: 'text-purple-600',
        iconStyle: 'triangle',
        type: 'Prophylaxis',
        frequency: '(Every 2-4 weeks)'
      },
      'Haegarda': {
        color: 'rgba(20, 184, 166, 1)',
        bgColor: 'bg-teal-50',
        textColor: 'text-teal-700',
        subTextColor: 'text-teal-600',
        iconStyle: 'circle',
        type: 'Prophylaxis',
        frequency: '(Twice weekly)'
      },
      'Icatibant': {
        color: 'rgba(251, 146, 60, 1)',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-700',
        subTextColor: 'text-orange-600',
        iconStyle: 'diamond',
        type: 'Acute treatment',
        frequency: ''
      },
      'Berinert': {
        color: 'rgba(34, 197, 94, 1)',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        subTextColor: 'text-green-600',
        iconStyle: 'rect',
        type: 'Acute treatment',
        frequency: ''
      },
      'Firazyr': {
        color: 'rgba(239, 68, 68, 1)',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        subTextColor: 'text-red-600',
        iconStyle: 'diamond',
        type: 'Acute treatment',
        frequency: ''
      },
      'Cinryze': {
        color: 'rgba(59, 130, 246, 1)',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        subTextColor: 'text-blue-600',
        iconStyle: 'triangle',
        type: 'Prophylaxis',
        frequency: '(As needed)'
      },
      'Orladeyo': {
        color: 'rgba(236, 72, 153, 1)',
        bgColor: 'bg-pink-50',
        textColor: 'text-pink-700',
        subTextColor: 'text-pink-600',
        iconStyle: 'cross',
        type: 'Oral prophylaxis',
        frequency: '(Daily)'
      }
    };

    // Show medications in the legend (Cinryze excluded)
    const allowedMedications = ['Takhzyro', 'Haegarda', 'Icatibant', 'Berinert', 'Firazyr', 'Orladeyo'];

    return allowedMedications
      .filter(medName => medicationConfig[medName]) // Only include configured medications
      .map(medName => {
        const config = medicationConfig[medName];
        return { name: medName, ...config };
      });
  };

  return (
    <div className="p-6">
      {/* Header with back button */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/patients')}
          className="mr-4 text-neutral hover:text-primary transition-colors duration-200"
        >
          <i className="fas fa-arrow-left text-xl"></i>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-primary">{patientName}</h1>
          <p className="text-neutral">Patient Number: {patientNumber}</p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-neutral hover:text-primary hover:border-gray-300'
              } transition-colors duration-200`}
            >
              <i className="fas fa-chart-pie mr-2"></i>
              Overview
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-neutral hover:text-primary hover:border-gray-300'
              } transition-colors duration-200`}
            >
              <i className="fas fa-history mr-2"></i>
              Historical Data
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-neutral text-lg">Loading patient overview...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-red-800 font-semibold mb-2">Error Loading Patient Data</h3>
                <p className="text-red-700 mb-4">{error}</p>
                <button
                  onClick={loadPatientOverview}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success State - Show Data */}
        {!loading && !error && overviewData && (
        <>
        {/* Patient Information Section */}
        <div className="mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-primary">Patient Information</h2>
            <button
              onClick={loadPatientOverview}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Personal Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-primary font-semibold mb-4">Personal Details</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Patient Name</span>
                    <p className="text-primary font-medium">{overviewData?.demographics?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Patient Number</span>
                    <p className="text-primary font-medium">{overviewData?.demographics?.patientNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Age</span>
                    <p className="text-primary font-medium">{overviewData?.demographics?.age || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Sex</span>
                    <p className="text-primary font-medium">{overviewData?.demographics?.sex || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Email</span>
                    <p className="text-primary font-medium">{overviewData?.demographics?.email || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Medical Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-primary font-semibold mb-4">Medical Information</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Type of HAE</span>
                    <p className="text-primary font-medium">{overviewData?.demographics?.typeOfHAE || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Date Diagnosed</span>
                    <p className="text-primary font-medium">
                      {overviewData?.demographics?.diagnosisDate
                        ? new Date(overviewData.demographics.diagnosisDate).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-primary font-semibold mb-4">Emergency Contact</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Name</span>
                    <p className="text-primary font-medium">{overviewData?.demographics?.emergencyName || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Phone</span>
                    <p className="text-primary font-medium">{overviewData?.demographics?.emergencyNumber || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Healthcare Providers */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-primary font-semibold mb-4">Healthcare Providers</h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-neutral block mb-1 text-sm">HCP1</span>
                    <p className="text-primary font-medium">{overviewData?.demographics?.hcp1Name || 'N/A'}</p>
                    <p className="text-primary text-sm">{overviewData?.demographics?.hcp1Phone || overviewData?.demographics?.hcp1Email || 'N/A'}</p>
                  </div>
                  {overviewData?.demographics?.hcp2Name && (
                    <div>
                      <span className="text-neutral block mb-1 text-sm">HCP2</span>
                      <p className="text-primary font-medium">{overviewData.demographics.hcp2Name}</p>
                      <p className="text-primary text-sm">{overviewData?.demographics?.hcp2Phone || overviewData?.demographics?.hcp2Email || 'N/A'}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Treatment Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-primary font-semibold mb-4">Treatment Information</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-neutral block mb-1 text-sm">Prescribed Treatment</span>
                    <p className="text-primary font-medium whitespace-pre-wrap">{overviewData?.demographics?.treatments || 'N/A'}</p>
                  </div>
                  {overviewData?.demographics?.otherTreatments && (
                    <div>
                      <span className="text-neutral block mb-1 text-sm">Other Treatments</span>
                      <p className="text-primary font-medium whitespace-pre-wrap">{overviewData.demographics.otherTreatments}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Visualizations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Areas Affected Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4 flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-primary">Areas Affected</h2>
              <p className="text-neutral text-sm mt-1">Distribution of HAE attacks by body region</p>
            </div>
            <div className="text-center bg-white bg-opacity-80 px-3 py-2 rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-primary inline-flex items-start justify-center">
                {overviewData?.timeBetweenAttacks?.totalAttacks || 0}<sup className="text-red-400 text-lg ml-0.5">*</sup>
              </div>
              <div className="text-neutral text-xs">Total attacks</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4">
            <div className="h-[280px] md:h-[320px] lg:h-[350px] relative">
              {areasAffectedCategorizedData.labels.length > 0 ? (
                <Doughnut data={areasAffectedCategorizedData} options={sharedDonutOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-neutral">No data available</div>
              )}
            </div>
          </div>
          <p className="text-xs text-neutral mt-3 italic">* Only events with reported "Area Affected" are considered</p>
        </div>

        {/* Medication HAE & Dose Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4 flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-primary">Medication HAE & Dose</h2>
              <p className="text-neutral text-sm mt-1">Distribution of medication usage</p>
            </div>
            <div className="text-center bg-white bg-opacity-80 px-3 py-2 rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-primary inline-flex items-start justify-center">
                {overviewData?.medicationStats?.filter(item => !excludedMedications.includes(item.medicationName.toLowerCase())).reduce((sum, item) => sum + item.usageCount, 0) || 0}<sup className="text-red-400 text-lg ml-0.5">*</sup>
              </div>
              <div className="text-neutral text-xs">Total uses</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4">
            <div className="h-[280px] md:h-[320px] lg:h-[350px] relative">
              {medicationHAEData.labels.length > 0 ? (
                <Doughnut data={medicationHAEData} options={sharedDonutOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-neutral">No data available</div>
              )}
            </div>
          </div>
          <p className="text-xs text-neutral mt-3 italic">* Only events with reported "HAE Medication" are considered</p>
        </div>

        {/* Triggers Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4 flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-primary">Triggers</h2>
              <p className="text-neutral text-sm mt-1">Distribution of trigger factors</p>
            </div>
            <div className="text-center bg-white bg-opacity-80 px-3 py-2 rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-primary inline-flex items-start justify-center">
                {overviewData?.triggerAnalysis?.reduce((sum, item) => sum + item.count, 0) || 0}<sup className="text-red-400 text-lg ml-0.5">*</sup>
              </div>
              <div className="text-neutral text-xs">Total cases</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4">
            <div className="h-[280px] md:h-[320px] lg:h-[350px] relative">
              {triggersData.labels.length > 0 ? (
                <Doughnut data={triggersData} options={sharedDonutOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-neutral">No data available</div>
              )}
            </div>
          </div>
          <p className="text-xs text-neutral mt-3 italic">* Only events with reported "Triggers" are considered</p>
        </div>

        {/* Treatment Classification Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4 flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-primary">Treatment Classification</h2>
              <p className="text-neutral text-sm mt-1">Distribution of HAE treatment types</p>
            </div>
            <div className="text-center bg-white bg-opacity-80 px-3 py-2 rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-primary">
                {overviewData?.treatmentClassification?.reduce((sum, item) => sum + item.count, 0) || 0}
              </div>
              <div className="text-neutral text-xs">Total treatments</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4">
            <div className="h-[280px] md:h-[320px] lg:h-[350px] relative">
              <Doughnut data={attacksData} options={sharedDonutOptions} />
            </div>
          </div>
        </div>

        {/* Days of Work/School Missed Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4 flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-primary">Days of Work/School Missed</h2>
              <p className="text-neutral text-sm mt-1">Distribution of missed days due to HAE attacks</p>
            </div>
            <div className="text-center bg-white bg-opacity-80 px-3 py-2 rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-primary">
                {missedDaysData.labels.length > 0 && missedDaysData.datasets[0].data.some(val => val > 0) ? 
                <>
                {overviewData?.totalDaysMissed ?? overviewData?.daysMissedDistribution?.reduce((sum, item) => sum + (item.totalDays ?? 0), 0) ?? 0}
                </>:"--"}
                
              </div>
              <div className="text-neutral text-xs">Total days missed</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4">
            <div className="h-[280px] md:h-[320px] lg:h-[350px] relative">
              {missedDaysData.labels.length > 0 && missedDaysData.datasets[0].data.some(val => val > 0) ? (
                <Doughnut data={missedDaysData} options={sharedDonutOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-neutral">No data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Treatment Location Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4 flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-primary">Treatment Location</h2>
              <p className="text-neutral text-sm mt-1">Distribution of treatment locations</p>
            </div>
            <div className="text-center bg-white bg-opacity-80 px-3 py-2 rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-primary inline-flex items-start justify-center">
                {overviewData?.treatmentLocations?.reduce((sum, item) => sum + item.count, 0) || 0}<sup className="text-red-400 text-lg ml-0.5">*</sup>
              </div>
              <div className="text-neutral text-xs">Total treatments</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4">
            <div className="h-[280px] md:h-[320px] lg:h-[350px] relative">
              {locationChartData.labels.length > 0 ? (
                <Doughnut data={locationChartData} options={sharedDonutOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-neutral">No data available</div>
              )}
            </div>
          </div>
          <p className="text-xs text-neutral mt-3 italic">* Only events with reported "Treatment Location" are considered</p>
        </div>
      </div>

      {/* Full Width Sections */}
      <div className="space-y-6 mb-6">
        {/* Body Region Impact - Full Width */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4 flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-primary">Body Region Impact</h2>
              <p className="text-neutral text-sm mt-1">Visual representation of affected areas</p>
            </div>
            <div className="text-center bg-white bg-opacity-80 px-3 py-2 rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-primary inline-flex items-start justify-center">
                {overviewData?.attackLocations?.length || 0}<sup className="text-red-400 text-lg ml-0.5">*</sup>
              </div>
              <div className="text-neutral text-xs">Affected regions</div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex justify-center items-center h-[700px] bg-gradient-to-br from-gray-50 to-white rounded-xl p-2">
              <div className="h-full w-full flex items-center justify-center">
                {overviewData?.attackLocations ? (
                  <HumanBody localizedData={overviewData.attackLocations.map(item => ({
                    class: item.location.toLowerCase().replace(/\s+/g, '-'),
                    percentage: parseFloat(item.percentage).toFixed(2)
                  }))} />
                ) : (
                  <div className="text-neutral">No body region data available</div>
                )}
              </div>
            </div>
            <div className="flex items-center bg-gradient-to-br from-gray-50 to-white rounded-xl p-4">
              <div className="h-[300px] w-full relative">
                {areasAffectedData.labels.length > 0 ? (
                  <Doughnut
                    data={areasAffectedData}
                    options={sharedDonutOptions}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-neutral">No data available</div>
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-neutral mt-3 italic">* Only events with reported "Area Affected" are considered</p>
        </div>

        {/* Signs and Symptoms Section - Full Width */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-primary">Signs and Symptoms<sup className="text-red-400 font-bold text-base">*</sup></h2>
            <p className="text-neutral text-sm mt-0.5">Distribution of most common symptoms</p>
          </div>
          <div className="h-[400px] px-4">
            <Bar data={barChartData} options={barChartOptions} />
          </div>
          <p className="text-xs text-neutral mt-3 italic px-4">* Only events with reported "Signs and Symptoms" are considered</p>
        </div>


      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Average Time Between Attack and Treatment */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-primary mb-4">Average time between attack and treatment<sup className="text-red-400 font-bold text-base">*</sup></h2>
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-center">
              {overviewData?.timeToTreatment?.averageMinutes ? (
                <>
                  <div className="text-6xl font-bold text-primary mb-2">
                    {overviewData.timeToTreatment.averageMinutes}
                  </div>
                  <div className="text-neutral">Mins</div>
                  {overviewData.timeToTreatment.totalAttacksWithTreatment > 0 && (
                    <div className="text-sm text-neutral mt-4">
                      Based on {overviewData.timeToTreatment.totalAttacksWithTreatment} attack(s) with treatment data
                    </div>
                  )}
                </>
              ) : (
                <div className="text-neutral text-center">
                  <div className="text-4xl mb-2">--</div>
                  <div className="text-sm">No treatment time data available</div>
                </div>
              )}
            </div>
          </div>
          {/* <p className="text-xs text-neutral mt-3 italic">* Only events with reported "time between attack and treatment" are considered</p> */}
        </div>
      </div>
        </>
        )}
        </>
      )}

      {/* Historical Data Tab */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          {/* Loading State */}
          {timelineLoading && (
            <div className="bg-white rounded-lg shadow p-12">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="text-neutral">Loading timeline data...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {timelineError && !timelineLoading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <i className="fas fa-exclamation-circle text-red-600 text-xl mt-1"></i>
                <div className="flex-1">
                  <h3 className="text-red-800 font-semibold mb-2">Error Loading Timeline</h3>
                  <p className="text-red-700">{timelineError}</p>
                  <button
                    onClick={loadPatientTimeline}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    <i className="fas fa-redo mr-2"></i>
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Timeline Data Display */}
          {!timelineLoading && !timelineError && timelineData && (
            <>
              {/* Timeline Controls */}
              <div className="bg-white rounded-lg shadow p-6">
                {/* Title Section */}
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-primary mb-2">
                    <i className="fas fa-chart-line mr-2"></i>
                    HAE Treatment Timeline
                  </h2>
                  <p className="text-neutral text-sm">
                    Comprehensive view of prophylaxis treatments, acute interventions, and attack intensity over time
                  </p>
                </div>

                {/* Date Range Filter - Separate Row */}
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="text-sm font-medium text-neutral whitespace-nowrap">
                      <i className="fas fa-calendar-alt mr-1"></i>
                      Date Range:
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setStartDate(newStartDate);
                          // Validate: if end date exists and is before start date, clear it
                          if (endDate && newStartDate && new Date(newStartDate) > new Date(endDate)) {
                            setEndDate('');
                          }
                        }}
                        max={endDate || undefined}
                        className="px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-white"
                        placeholder="Start Date"
                      />
                      <span className="text-neutral">to</span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          setEndDate(newEndDate);
                          // Validate: if start date exists and is after end date, clear it
                          if (startDate && newEndDate && new Date(startDate) > new Date(newEndDate)) {
                            setStartDate('');
                          }
                        }}
                        min={startDate || undefined}
                        className="px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-white"
                        placeholder="End Date"
                      />
                      {(startDate || endDate) && (
                        <button
                          onClick={() => {
                            setStartDate('');
                            setEndDate('');
                          }}
                          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-neutral rounded-lg text-sm transition-colors"
                          title="Clear filters"
                        >
                          <i className="fas fa-times mr-1"></i> Clear
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Validation Message */}
                  {startDate && endDate && new Date(startDate) > new Date(endDate) && (
                    <div className="mt-2 text-sm text-red-600">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      Start date must be before end date
                    </div>
                  )}
                </div>

                {/* Legend and Visual Guide */}
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-primary mb-3">Timeline Legend:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {getMedicationLegendConfig().map((med, index) => (
                      <div key={index} className={`flex items-center space-x-3 p-3 ${med.bgColor} rounded-lg`}>
                        {med.iconStyle === 'triangle' && (
                          <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[10px] border-l-transparent border-r-transparent" style={{borderBottomColor: med.color}}></div>
                        )}
                        {med.iconStyle === 'circle' && (
                          <div className="w-3 h-3 rounded-full" style={{backgroundColor: med.color}}></div>
                        )}
                        {med.iconStyle === 'diamond' && (
                          <div className="w-3 h-3 transform rotate-45" style={{backgroundColor: med.color}}></div>
                        )}
                        {med.iconStyle === 'rect' && (
                          <div className="w-3 h-3" style={{backgroundColor: med.color}}></div>
                        )}
                        {med.iconStyle === 'cross' && (
                          <div className="relative w-3 h-3">
                            <div className="absolute top-1/2 left-0 w-full h-0.5 transform -translate-y-1/2 rotate-45" style={{backgroundColor: med.color}}></div>
                            <div className="absolute top-1/2 left-0 w-full h-0.5 transform -translate-y-1/2 -rotate-45" style={{backgroundColor: med.color}}></div>
                          </div>
                        )}
                        <div>
                          <div className={`text-sm font-medium ${med.textColor}`}>{med.name}</div>
                          <div className={`text-xs ${med.subTextColor}`}>{med.type} {med.frequency}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-1 bg-red-500"></div>
                      <div className="text-sm font-medium text-red-700">Attack Intensity</div>
                    </div>
                    <div className="text-xs text-red-600">Red stepped line showing severity (0-10 scale)</div>
                  </div>
                </div>
              </div>

          {/* Timeline Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="h-[600px]">
              <Line data={createTimelineChartData()} options={getTimelineChartOptions()} />
            </div>
          </div>

          {/* Treatment Summary Statistics */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Prophylaxis Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-primary">
                  <i className="fas fa-shield-alt mr-2 text-blue-500"></i>
                  Prophylaxis
                </h3>
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Total Treatments:</span>
                  <span className="font-medium text-primary">{getFilteredTimelineData().prophylaxis.filter(p => p.type !== 'prodrome').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Current Medication:</span>
                  <span className="font-medium text-primary">
                    {(() => {
                      const prophylaxisOnly = getFilteredTimelineData().prophylaxis.filter(p => p.type !== 'prodrome');
                      return prophylaxisOnly.length > 0
                        ? prophylaxisOnly[prophylaxisOnly.length - 1].medication
                        : 'N/A';
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Takhzyro Doses:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().prophylaxis.filter(p => p.type !== 'prodrome').reduce((count, p) => {
                      const allMedsCount = p.allMedications?.filter(m => m.name === 'Takhzyro').length || 0;
                      const legacyCount = !p.allMedications?.length && p.medication === 'Takhzyro' ? 1 : 0;
                      return count + allMedsCount + legacyCount;
                    }, 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Haegarda Doses:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().prophylaxis.filter(p => p.type !== 'prodrome').reduce((count, p) => {
                      const allMedsCount = p.allMedications?.filter(m => m.name === 'Haegarda').length || 0;
                      const legacyCount = !p.allMedications?.length && p.medication === 'Haegarda' ? 1 : 0;
                      return count + allMedsCount + legacyCount;
                    }, 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Berinert Doses:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().prophylaxis.filter(p => p.type !== 'prodrome').reduce((count, p) => {
                      const allMedsCount = p.allMedications?.filter(m => m.name === 'Berinert').length || 0;
                      const legacyCount = !p.allMedications?.length && p.medication === 'Berinert' ? 1 : 0;
                      return count + allMedsCount + legacyCount;
                    }, 0)}
                  </span>
                </div>
                {/* Cinryze Doses hidden
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Cinryze Doses:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().prophylaxis.filter(p => p.type !== 'prodrome').reduce((count, p) => {
                      const allMedsCount = p.allMedications?.filter(m => m.name === 'Cinryze').length || 0;
                      const legacyCount = !p.allMedications?.length && p.medication === 'Cinryze' ? 1 : 0;
                      return count + allMedsCount + legacyCount;
                    }, 0)}
                  </span>
                </div>
                */}
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Orladeyo Doses:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().prophylaxis.filter(p => p.type !== 'prodrome').reduce((count, p) => {
                      const allMedsCount = p.allMedications?.filter(m => m.name === 'Orladeyo').length || 0;
                      const legacyCount = !p.allMedications?.length && p.medication === 'Orladeyo' ? 1 : 0;
                      return count + allMedsCount + legacyCount;
                    }, 0)}
                  </span>
                </div>
                {/* <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="text-neutral text-sm">Prodrome Events:</span>
                  <span className="font-medium text-orange-600">
                    {getFilteredTimelineData().prophylaxis.filter(p => p.type === 'prodrome').length}
                  </span>
                </div> */}
              </div>
            </div>

            {/* Acute Treatments Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-primary">
                  <i className="fas fa-exclamation-triangle mr-2 text-red-500"></i>
                  Acute Treatments
                </h3>
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Total Treatments:</span>
                  <span className="font-medium text-primary">{getFilteredTimelineData().acute.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Icatibant Uses:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().acute.reduce((count, a) => {
                      const allMedsCount = a.allMedications?.filter(m => m.name === 'Icatibant').length || 0;
                      const legacyCount = !a.allMedications?.length && a.medication === 'Icatibant' ? 1 : 0;
                      return count + allMedsCount + legacyCount;
                    }, 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Berinert Uses:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().acute.reduce((count, a) => {
                      const allMedsCount = a.allMedications?.filter(m => m.name === 'Berinert').length || 0;
                      const legacyCount = !a.allMedications?.length && a.medication === 'Berinert' ? 1 : 0;
                      return count + allMedsCount + legacyCount;
                    }, 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Firazyr Uses:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().acute.reduce((count, a) => {
                      const allMedsCount = a.allMedications?.filter(m => m.name === 'Firazyr').length || 0;
                      const legacyCount = !a.allMedications?.length && a.medication === 'Firazyr' ? 1 : 0;
                      return count + allMedsCount + legacyCount;
                    }, 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Emergency Room:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().acute.filter(a => a.location?.toLowerCase() === 'emergency room').length}
                  </span>
                </div>
              </div>
            </div>

            {/* Attack Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-primary">
                  <i className="fas fa-chart-line mr-2 text-orange-500"></i>
                  Attack Summary
                </h3>
                <div className="w-4 h-4 bg-gradient-to-r from-green-400 to-red-500 rounded-full"></div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Total Attacks:</span>
                  <span className="font-medium text-primary">{getFilteredTimelineData().attacks.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Avg Intensity:</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().attacks.length > 0 
                      ? (getFilteredTimelineData().attacks.reduce((sum, a) => sum + a.intensity, 0) / getFilteredTimelineData().attacks.length).toFixed(1)
                      : '0'}/10
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Severe (8-10):</span>
                  <span className="font-medium text-primary">
                    {getFilteredTimelineData().attacks.filter(a => a.intensity >= 8).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral text-sm">Most Common Trigger:</span>
                  <span className="font-medium text-primary text-xs">
                    {(() => {
                      const attacks = getFilteredTimelineData().attacks;
                      if (attacks.length === 0) return 'N/A';

                      // Count each individual trigger (triggers can be comma-separated string)
                      const triggerCounts = attacks.reduce((acc, attack) => {
                        if (!attack.triggers || attack.triggers === 'Unknown') return acc;

                        // Split by comma and count each trigger individually
                        const triggerList = attack.triggers.split(',').map(t => t.trim()).filter(t => t);
                        triggerList.forEach(trigger => {
                          acc[trigger] = (acc[trigger] || 0) + 1;
                        });
                        return acc;
                      }, {});

                      const mostCommon = Object.entries(triggerCounts)
                        .sort(([,a], [,b]) => b - a)[0];

                      return mostCommon ? `${mostCommon[0]} (${mostCommon[1]})` : 'N/A';
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Timeline Events */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-primary mb-4">
              <i className="fas fa-list-ul mr-2"></i>
              Recent Timeline Events
            </h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {/* Combine and sort events - Only show Prophylaxis, Attack, and Prodrome (Cinryze filtered out) */}
              {[
                ...getFilteredTimelineData().prophylaxis.map(item => ({
                  ...item,
                  eventType: item.type === 'prodrome' ? 'prodrome' : 'prophylaxis',
                  sortDate: new Date(item.date + 'T12:00:00')
                })),
                ...getFilteredTimelineData().attacks.map(item => ({ ...item, eventType: 'attack', sortDate: new Date(item.date + 'T12:00:00') }))
              ]
                .filter(event => !event.medication || event.medication.toLowerCase() !== 'cinryze')
                .sort((a, b) => b.sortDate - a.sortDate)
                .slice(0, 10) // Show only recent 10 events
                .map((event, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                    <div className={`w-3 h-3 rounded-full mt-2 flex-shrink-0 ${
                      event.eventType === 'prophylaxis' ? 'bg-blue-500' :
                      event.eventType === 'prodrome' ? 'bg-yellow-500' :
                      'bg-orange-500'
                    }`}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-primary">
                          {new Date(event.date + 'T12:00:00').toLocaleDateString('en-US', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                          {event.time && <span className="text-neutral text-sm ml-2">at {event.time}</span>}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          event.eventType === 'prophylaxis' ? 'bg-blue-100 text-blue-800' :
                          event.eventType === 'prodrome' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          {event.eventType === 'prophylaxis' ? 'Prophylaxis' :
                           event.eventType === 'prodrome' ? 'Prodrome' : 'Attack'}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-neutral">
                        {event.eventType === 'prophylaxis' && (
                          <span><strong>{event.medication}</strong> - {event.dosage}  </span>
                        )}
                        {event.eventType === 'prodrome' && (
                          <span><strong>{event.medication}</strong> - {event.dosage}  </span>
                        )}
                        {event.eventType === 'attack' && (
                          <span>Intensity: <strong>{event.intensity}/10</strong>, Duration: {event.duration}, Trigger: {event.triggers}</span>
                        )}
                      </div>
                      {event.notes && (
                        <div className="mt-1 text-xs text-gray-600 italic">{event.notes}</div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PatientDetail; 